import React, { Fragment, useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Header, Icon, Button, Card } from 'semantic-ui-react';
import i18next from 'i18next';
import EventsCalendar from '../../common/EventsCalendar/EventsCalendar';
import SideEditor from '../../general/SideEditor';
import MarketingCampaignEditor from './MarketingCampaignEditor';
import BrandBuilder from '../brands/BrandBuilder';
import { setEditedEntity, fetchMarketingCampaigns } from '../../../actions';
import { getCurrentChain, getCurrentRestaurant } from '../../../selectors';

import 'react-big-calendar/lib/sass/styles.scss';
import './MarketingCampaigns.scss';

const scrollToRef = (ref) => {
    ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
    });
};

const MarketingCampaigns = (props) => {
    const [eventOnEdit, setEventOnEdit] = useState(null);
    const dispatch = useDispatch();
    const chain = useSelector(getCurrentChain);
    const restaurant = useSelector(getCurrentRestaurant);
    const editedType = useSelector(state => state.editor.type);
    const events = useSelector(state => state.businessApp.marketingCampaigns);
    const brandBuilderRef = useRef(null);

    const chainId = chain ? chain.id : restaurant.ownerId;
    const restId = restaurant ? restaurant.id : null;

    useEffect(() => {
        dispatch(fetchMarketingCampaigns(chainId, restId, []));
    }, [chain, restaurant]);

    const isMultibranchCampaign = (campaign) => {
        return campaign.id && campaign.branchIds && (campaign.branchIds.length === 0 || campaign.branchIds.includes(restId) && campaign.branchIds.length > 1);
    };

    return (
        <Fragment>
            <Header.Content className="page-header">{i18next.t('marketingCampaignsPage.title')}</Header.Content>
            <Header.Content className="page-sub-header">{i18next.t('marketingCampaignsPage.description')}</Header.Content>
            <div className="create-campaign-btn">
                <Button
                    icon
                    labelPosition='left'
                    floated="right"
                    primary
                    onClick={() => dispatch(setEditedEntity('marketingCampaign'))}
                >
                    <Icon name='add' /> {i18next.t('marketingCampaignsPage.createCampaign')}
                </Button>
            </div>
            <div className="pi-events-calendar">
                <EventsCalendar events={events} handleEventClick={(event) => {
                    setEventOnEdit(event);
                    dispatch(setEditedEntity('marketingCampaign', event));
                }} />
            </div>

            <SideEditor
                title={i18next.t('campaign')}
                visible={editedType === 'marketingCampaign'}
                handleClose={() => dispatch(setEditedEntity(null))}
            >
                <MarketingCampaignEditor 
                    isMultibranchCampaign={isMultibranchCampaign}
                    handleSave={(entity) => {
                        dispatch(setEditedEntity(null));
                        setEventOnEdit(entity);
                        scrollToRef(brandBuilderRef);
                    }}
                    handleDelete={() => {
                        setEventOnEdit(null);
                        dispatch(setEditedEntity(null));
                    }} 
                />
            </SideEditor>

            <div ref={brandBuilderRef} className="pi campain-details-container">
                {eventOnEdit && (
                    <Card className="pi campaign-details">
                        <Card.Header className="campaign-header">
                            <div className="campaign-description">
                                <span>
                                    {eventOnEdit.title}
                                    <span className="campaign-dates">({moment(eventOnEdit.start).format('DD/MM/YYYY')}-{moment(eventOnEdit.end).format('DD/MM/YYYY')})</span>
                                </span>
                                <span className="campaign-meta">{eventOnEdit.description}</span>
                            </div>
                            <div>
                                <Button icon labelPosition='left' negative floated="right" primary onClick={() => setEventOnEdit(null)}>
                                    <Icon name='x' /> {i18next.t('close')}
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Content>
                            {restId && isMultibranchCampaign(eventOnEdit) && <div className="overlay-disabled" />}
                            <BrandBuilder match={props.match} brandKey={eventOnEdit.brandKey} handleBrandLoaded={() => scrollToRef(brandBuilderRef)} />
                        </Card.Content>
                    </Card>
                )}
            </div>
        </Fragment>
    );
};

MarketingCampaigns.propTypes = {
    match: PropTypes.object,
};

export default MarketingCampaigns;